import React, { useEffect, useState } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { transportApi } from '@/services/transportApi';
import { ownershipParams } from '@/components/transport/DashboardBits';
import { fmtMin } from './ReportKpis';

const TIMES = [['loading', 'Loading', '#2563EB'], ['pickup_stop', 'At pickup stop', '#0D9488'],
  ['unloading', 'Unloading', '#7C3AED'], ['drop_stop', 'At drop stop', '#64748B']];

const ChartCard = ({ title, testId, children }) => (
  <Card className="border-0 shadow-md" data-testid={testId}><CardContent className="p-4">
    <h3 className="font-semibold text-sm text-[#1A1F2E] mb-2">{title}</h3>
    <div className="h-64"><ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer></div>
  </CardContent></Card>
);

const TrendTable = ({ rows }) => (
  <Card className="border-0 shadow-md"><CardContent className="p-0">
    <Table data-testid="trend-table">
      <TableHeader><TableRow>
        <TableHead>Month</TableHead><TableHead>Distance (km)</TableHead><TableHead>Litres</TableHead><TableHead>Km/L</TableHead>
        {TIMES.map(([k, l]) => <TableHead key={k}>{l}</TableHead>)}
      </TableRow></TableHeader>
      <TableBody>
        {rows.map(r => (
          <TableRow key={r.month} data-testid={`trend-row-${r.month}`}>
            <TableCell className="font-medium">{r.label}</TableCell>
            <TableCell>{r.km ? r.km.toLocaleString('en-IN') : '—'}</TableCell>
            <TableCell>{r.litres || '—'}</TableCell>
            <TableCell className="font-semibold" data-testid={`trend-kmpl-${r.month}`}>{r.kmpl ?? '—'}</TableCell>
            {TIMES.map(([k]) => <TableCell key={k} data-testid={`trend-${k}-${r.month}`}>{fmtMin(r[k])}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </CardContent></Card>
);

export const MonthlyTrendReport = ({ own, vehicleId, onVehicle }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const p = { ...ownershipParams(own), ...(vehicleId !== 'all' ? { vehicle_id: vehicleId } : {}) };
    transportApi.reportMonthlyTrend(p).then(setData).catch(() => toast.error('Failed to load monthly trend'));
  }, [own, vehicleId]);
  const rows = (data?.months || []).map(m => ({
    ...m, loading: m.load_trip?.avg_min, pickup_stop: m.load_stop?.avg_min,
    unloading: m.unload_trip?.avg_min, drop_stop: m.unload_stop?.avg_min,
  }));
  return (
    <div className="space-y-4" data-testid="trend-report">
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <Select value={vehicleId} onValueChange={onVehicle}>
          <SelectTrigger className="w-56 bg-white" data-testid="trend-vehicle-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="trend-vehicle-opt-all">All vehicles</SelectItem>
            {(data?.vehicles || []).map(v => <SelectItem key={v.id} value={v.id} data-testid={`trend-vehicle-opt-${v.id}`}>{v.registration_no}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-gray-500">Last 12 months · the From/To dates don't apply here</span>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <ChartCard title="Km/L by month (fuel odometer)" testId="trend-kmpl-chart">
          <LineChart data={rows} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => [`${v} km/L`, 'Km/L']} />
            <Line type="monotone" dataKey="kmpl" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3 }} />
          </LineChart>
        </ChartCard>
        <ChartCard title="Average loading / unloading time (minutes)" testId="trend-time-chart">
          <LineChart data={rows} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, n) => [fmtMin(v), n]} /><Legend wrapperStyle={{ fontSize: 11 }} />
            {TIMES.map(([k, l, c]) => <Line key={k} type="monotone" dataKey={k} name={l} stroke={c} strokeWidth={2} dot={{ r: 2 }} />)}
          </LineChart>
        </ChartCard>
      </div>
      <TrendTable rows={rows} />
      <p className="text-[11px] text-gray-400">Each month's Km/L = that month's last fill odometer − its first fill odometer ÷ litres filled after the first fill, so a month needs at least 2 fills with an odometer reading.</p>
    </div>
  );
};
